import { useState, useEffect } from 'react';
import {
  applyTheme,
  getNavbarColor,
  toggleDarkMode,
  isDarkMode,
} from './tailwind-config/theme';

/**
 * Manages theme and dark-mode state for a v2 app.
 *
 * - Applies the default theme on mount.
 * - In dev mode, exposes window.setTheme() and window.toggleDarkMode()
 *   for console testing.
 *
 * @param {string} defaultTheme - Theme to apply on mount (e.g. 'pink').
 * @returns {{ theme: string, darkMode: boolean, navbarColor: string }}
 */
export function useTheme(defaultTheme = 'pink') {
  const [theme, setTheme] = useState(defaultTheme);
  const [darkMode, setDarkMode] = useState(isDarkMode());

  // Apply default theme on mount
  useEffect(() => {
    applyTheme(defaultTheme);
  }, [defaultTheme]);

  // Expose window helpers in dev only
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    window.setTheme = name => {
      const applied = applyTheme(name, defaultTheme);
      setTheme(applied);
    };
    window.toggleDarkMode = () => {
      const dark = toggleDarkMode();
      setDarkMode(dark);
    };
    return () => {
      delete window.setTheme;
      delete window.toggleDarkMode;
    };
  }, [defaultTheme]);

  return {
    theme,
    darkMode,
    navbarColor: getNavbarColor(theme),
  };
}

/**
 * Open the sign-in flow. In the Django shell `window.showLoginForm` opens the
 * login modal; falls back to a direct URL for standalone dev.
 */
export function signIn() {
  if (typeof window === 'undefined') return;
  if (typeof window.showLoginForm === 'function') {
    window.showLoginForm();
  } else if (typeof window.revealModal === 'function') {
    // Legacy pages don't define showLoginForm but do expose revealModal (from
    // common_modals) — open the login modal the same way signUp() does.
    window.revealModal('logIn');
  } else {
    window.location.assign('/login/');
  }
}

/**
 * Open the sign-up flow — the registration ("Sign Up to get started / Choose
 * your role") modal, distinct from the login modal. Falls back to /register/.
 */
export function signUp() {
  if (typeof window !== 'undefined' && typeof window.revealModal === 'function') {
    window.revealModal('registrationModal');
  } else {
    window.location.assign('/register/');
  }
}

/**
 * Send the user to the pricing page — used by the "Start free trial" / "Start
 * trial" / "Upgrade" premium-upsell CTAs.
 */
export function goToPricing() {
  window.location.assign('/pricing/');
}
